import { useCallback } from 'react';
import type { GroupMemberSignUpMethod } from '@group/domain/GroupEntity';
import { SignUpMethodName } from '@group/domain/GroupEntity';
import { useTranslation } from 'react-i18next';

type Props = {
  signUpMethod: GroupMemberSignUpMethod;
  onChange(signUpMethod: GroupMemberSignUpMethod): void;
};

export const AvailableSignUpMethodForm: React.FC<Props> = ({ signUpMethod, onChange }: Props) => {
  const { t } = useTranslation();
  const handleChange = useCallback(
    (name: SignUpMethodName, checked: boolean) => {
      onChange(signUpMethod.with(name, checked));
    },
    [onChange, signUpMethod]
  );

  const visibleMethods = SignUpMethodName.values();
  const isEmpty = signUpMethod.isEmpty();

  return (
    <>
      <ul className="flex flex-col">
        {visibleMethods.map((name) => {
          return (
            <SignUpMethod key={name} name={name} checked={signUpMethod.availableOf(name)} onChange={handleChange} />
          );
        })}
      </ul>
      {isEmpty && <div className="text-red-500">{t('group.signUpMethod.emptyError')}</div>}
    </>
  );
};

type ItemProps = {
  name: SignUpMethodName;
  checked: boolean;
  onChange(name: SignUpMethodName, checked: boolean): void;
};

const SignUpMethod: React.FC<ItemProps> = ({ name, checked, onChange }: ItemProps) => {
  const { t } = useTranslation();
  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onChange(name, event.target.checked);
    },
    [name, onChange]
  );

  return (
    <li className="flex flex-row items-center p-2 hover:bg-gray-200">
      <label className="flex grow cursor-pointer flex-row items-center">
        <input
          type="checkbox"
          className="form-checkbox size-4 cursor-pointer border-brand"
          checked={checked}
          onChange={handleChange}
        />
        <span className="grow px-2">{t(SignUpMethodName.getLocalStringKey(name))}</span>
      </label>
    </li>
  );
};
